import { MapPin, Phone, Smartphone, Mail, MessageCircle, Clock, FileBadge } from "lucide-react";
import Seo from "@/components/Seo";
import { PageHero } from "@/components/blocks";
import { LeadForm } from "@/components/LeadForm";
import { Reveal } from "@/components/Reveal";
import { COMPANY, wa, WA_MESSAGES, REQUIREMENT_OPTIONS } from "@/data/site";
import { track } from "@/lib/analytics";
import { breadcrumbJsonLd } from "@/lib/seo";

const INFO = [
  {
    icon: MapPin,
    label: "Address",
    content: (
      <address className="not-italic leading-relaxed">
        {COMPANY.name}<br />
        {COMPANY.addressLines.map((l) => (
          <span key={l}>{l}<br /></span>
        ))}
      </address>
    ),
    testId: "contact-address",
  },
  {
    icon: Phone,
    label: "Office Phone",
    content: <a href={COMPANY.phoneHref} onClick={() => track("phone_click", { location: "contact" })} className="hover:text-cts-red transition-colors">{COMPANY.phone}</a>,
    testId: "contact-phone",
  },
  {
    icon: Smartphone,
    label: "Mobile / WhatsApp",
    content: (
      <>
        <a href={COMPANY.mobileHref} onClick={() => track("phone_click", { location: "contact" })} className="hover:text-cts-red transition-colors">{COMPANY.mobile}</a>
        {" · "}
        <a href={wa(WA_MESSAGES.general)} target="_blank" rel="noopener noreferrer" onClick={() => track("whatsapp_click", { location: "contact" })} className="hover:text-cts-red transition-colors">WhatsApp</a>
      </>
    ),
    testId: "contact-mobile",
  },
  {
    icon: Mail,
    label: "Email",
    wide: true,
    content: <a href={`mailto:${COMPANY.email}`} className="hover:text-cts-red transition-colors break-all">{COMPANY.email}</a>,
    testId: "contact-email",
  },
  {
    icon: Clock,
    label: "Opening Hours",
    content: (
      <span className="text-cts-charcoal">
        Mon – Sat: 9:30 AM – 6:30 PM
        <br />
        Sunday: Closed
      </span>
    ),
    testId: "contact-hours",
  },
  {
    icon: FileBadge,
    label: "GST",
    content: <span>{COMPANY.gst}</span>,
    testId: "contact-gst",
  },
];

export default function Contact() {
  return (
    <>
      <Seo
        title="Contact CTS | Mallappally, Pathanamthitta"
        siteName={COMPANY.brand}
        description="Contact CTS Mallappally for computers, printers, toners, networking, CCTV installation or technical service. Call 062380 98067 or send an enquiry."
        jsonLd={breadcrumbJsonLd([{ label: "Contact" }])}
      />
      <PageHero
        eyebrow="CONTACT"
        title="Contact"
        intro="Need a computer, printer, toner, networking solution, CCTV installation or technical service? Get in touch with CTS."
        crumbs={[{ label: "Contact" }]}
      />
      <section className="section-pad bg-cts-soft">
        <div className="container-site grid gap-10 lg:grid-cols-2">
          <Reveal>
            <div className="grid gap-4 sm:grid-cols-2">
              {INFO.map((item) => (
                <div key={item.label} className={`rounded-lg border border-cts-line bg-white p-5 ${item.wide ? "sm:col-span-2" : ""}`} data-testid={item.testId}>
                  <div className="flex items-center gap-2.5 mb-2">
                    <item.icon size={18} className="text-cts-red" />
                    <h2 className="font-heading font-bold text-sm uppercase tracking-wide text-cts-ink">{item.label}</h2>
                  </div>
                  <div className="text-[15px] text-cts-charcoal">{item.content}</div>
                </div>
              ))}
            </div>
            <div className="mt-6 rounded-lg overflow-hidden border border-cts-line bg-white">
              <iframe
                title="Cherians Tech Solutions location map — Mallappally"
                src={COMPANY.mapsEmbed}
                className="w-full h-72"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="contact-map"
              />
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="rounded-xl border border-cts-line bg-white p-6 md:p-8 h-fit" data-testid="contact-form-card">
              <p className="eyebrow mb-2">SEND AN ENQUIRY</p>
              <h2 className="h3-display mb-6">How Can We Help?</h2>
              <LeadForm
                formType="contact"
                requirementOptions={REQUIREMENT_OPTIONS}
                fields={["name", "phone", "location", "message"]}
                customFields={[
                  { name: "preferred_contact", label: "Preferred Contact Method (optional)", type: "select", options: ["Phone", "WhatsApp", "Email"] },
                ]}
                submitLabel="SEND ENQUIRY"
                trackEvent="quote_submitted"
                sourceCta="Contact page form"
                testIdPrefix="contact-form"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
